/**
 * The voice connection to one other room member: an RTCPeerConnection (through PeerPort)
 * and its negotiation.
 *
 * Roles, with polite = (myId < peerId) as in "perfect negotiation":
 *  - The impolite side is the offerer: once the player turned voice on, it creates the
 *    connection with the audio transceiver, which starts the negotiation.
 *  - The polite side never adds a transceiver of its own. It answers the offer and takes
 *    over the offered transceiver: one it had added itself would not match the offer's
 *    audio section and would need a second negotiation round. It can ask for an offer
 *    with a "request" — an `rtc` description of type `rollback`, a type that never travels
 *    otherwise — when the member (re)appears and when its connection failed.
 *  - Later renegotiations (ICE restarts) are perfect negotiation: either side may offer,
 *    a collision is resolved by politeness (the polite side rolls back).
 * Before the player turns voice on, the link stays passive but still answers offers, so
 * turning voice on later is instant (playback is muted until then).
 *
 * Recovery (signals are dropped while a WebSocket is down, phones change networks):
 *  - A description with a new SDP session id means the other side started over (reload,
 *    it saw us drop out): we start over as well.
 *  - The offerer re-sends an unanswered offer; a re-sent offer gets the same answer again.
 *    A request for an offer that already went out a while ago re-sends it once more.
 *  - Connections are fixed by the offerer: on "failed", after a "disconnected" that lasts,
 *    or when a connection is not up `connectTimeoutMs` after it started (ICE that never
 *    even begins, e.g. no usable candidates, never reports "failed"), it restarts ICE (with
 *    fresh TURN credentials), and if that does not help, starts over. The polite side gives
 *    it a head start and then asks with a request.
 *
 * ICE candidates found within a short window after setLocalDescription travel inside the
 * description (one message instead of several), later ones trickle through the paced
 * signal queue. Signals of one link are handled strictly one after another.
 */
import type { IceServer, PlayerId, RtcSignal } from '@cube-rush/shared';
import type {
  IceCandidate,
  MicCapture,
  PeerPort,
  RemoteAudio,
  SessionDescription,
  VoiceMedia,
} from './media';
import { sdpIceUfrag, sdpSessionId } from './sdp';
import type { SignalPriority } from './signalQueue';
import { SpeakingDetector } from './speaking';
import type { VoicePeerState } from './types';

export interface PeerLinkContext {
  readonly media: VoiceMedia;
  /** STUN/TURN servers, read for every new connection and ICE restart. */
  iceServers(): readonly IceServer[];
  /** Sends a signal through the paced queue; `stale` says when it is no longer worth sending. */
  send(to: PlayerId, data: RtcSignal, priority: SignalPriority, stale: () => boolean): void;
  /** The microphone for new connections (null: none yet). */
  mic(): MicCapture | null;
  /** The player turned voice on: this side may start connections. */
  active(): boolean;
  /** The link's state or remote audio changed. */
  changed(link: PeerLink): void;
}

export const LINK_TIMING = {
  /** Candidates found this soon after setLocalDescription go inside the description, ms. */
  gatherWindowMs: 400,
  /** Re-sends of an unanswered offer, ms after the previous one. */
  offerRetryMs: [3000, 6000, 12000] as readonly number[],
  /** A "disconnected" connection gets this long to come back by itself, ms. */
  disconnectGraceMs: 4000,
  /** The polite side waits this long for the offerer's ICE restart before asking, ms. */
  politeRecoveryMs: 2500,
  /**
   * A connection that is not up this long after it was created (or restarted) counts as
   * failed — also when ICE never even starts (no usable candidates); doubles with every
   * further attempt, up to four times, ms.
   */
  connectTimeoutMs: 15000,
  maxPendingCandidates: 64,
};

const REQUEST: RtcSignal = { kind: 'description', description: { type: 'rollback' } };

export class PeerLink {
  readonly polite: boolean;
  readonly speaking = new SpeakingDetector();
  private port: PeerPort | null = null;
  private generation = 0;
  private stateValue: VoicePeerState = 'new';
  private remote: RemoteAudio | null = null;
  private volumeValue = 1;
  private closed = false;
  // Perfect negotiation.
  private makingOffer = false;
  private settingRemoteAnswer = false;
  // Recovery.
  private remoteSession: string | null = null;
  private lastRemoteOffer: string | null = null;
  private pendingCandidates: IceCandidate[] = [];
  private descriptionSent = false;
  private sendPending: (() => void) | null = null;
  private gatherTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private recoveries = 0;
  private offerSentAt = 0;
  private recoveredAt = 0;
  /** Signals and negotiations run one after another. */
  private queue: Promise<void> = Promise.resolve();

  constructor(
    readonly peerId: PlayerId,
    myId: PlayerId,
    private readonly ctx: PeerLinkContext,
  ) {
    this.polite = myId < peerId;
  }

  get state(): VoicePeerState {
    return this.stateValue;
  }

  get volume(): number {
    return this.volumeValue;
  }

  /** The other player's voice arrives (it may be silent). */
  get hasAudio(): boolean {
    return this.remote !== null;
  }

  /** Level of the other player's voice, dBFS. */
  level(): number {
    return this.remote ? this.remote.level() : Number.NEGATIVE_INFINITY;
  }

  /** The member appeared in the room. */
  start(): void {
    if (!this.ctx.media.rtcSupported) return; // no WebRTC: the link only shows the member
    if (this.polite) this.requestOffer();
    else if (this.ctx.active()) this.connect();
  }

  /** The player turned voice on. */
  activate(): void {
    if (!this.polite && !this.port && !this.closed && this.ctx.media.rtcSupported) this.connect();
  }

  setMic(mic: MicCapture | null): void {
    this.port?.setMic(mic);
  }

  setVolume(volume: number): void {
    this.volumeValue = volume;
    this.remote?.setVolume(volume);
  }

  handleSignal(data: RtcSignal): Promise<void> {
    return this.enqueue(async () => {
      if (data.kind === 'description') await this.onDescription(data.description);
      else if (data.candidate) await this.onRemoteCandidate(data.candidate);
    });
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.clearTimer('recoveryTimer');
    this.closePort();
    this.stateValue = 'closed';
  }

  // ---- connection lifecycle ----

  /** Offerer: a fresh connection with the audio transceiver; negotiation follows. */
  private connect(): void {
    const port = this.openPort();
    port.addAudioTransceiver();
    port.setMic(this.ctx.mic());
  }

  /** Polite side: forget the connection and ask the offerer for a fresh one. */
  private startOver(): void {
    if (this.polite) {
      this.closePort();
      this.setState('new');
      this.requestOffer();
    } else if (this.ctx.active()) {
      this.connect();
    } else {
      this.closePort();
      this.setState('new');
    }
  }

  private openPort(): PeerPort {
    this.closePort();
    const generation = ++this.generation;
    const current = () => !this.closed && generation === this.generation;
    const port = this.ctx.media.createPeer(this.ctx.iceServers(), {
      negotiationNeeded: () => {
        if (current()) void this.enqueue(() => this.negotiate(port));
      },
      iceCandidate: (candidate) => {
        if (current()) this.onLocalCandidate(port, candidate);
      },
      remoteAudio: (audio) => {
        if (current()) this.attachRemote(audio);
        else audio.dispose();
      },
      connectionState: (state) => {
        if (current()) this.onConnectionState(state);
      },
    });
    this.port = port;
    this.remoteSession = null;
    this.lastRemoteOffer = null;
    this.pendingCandidates = [];
    this.descriptionSent = false;
    this.offerSentAt = 0;
    this.makingOffer = false;
    this.setState(port.connectionState);
    this.armWatchdog(port);
    return port;
  }

  private closePort(): void {
    this.clearTimer('gatherTimer');
    this.clearTimer('retryTimer');
    this.clearTimer('connectTimer');
    this.sendPending = null;
    const port = this.port;
    this.port = null;
    this.generation++;
    port?.close();
    if (this.remote) {
      this.remote.dispose();
      this.remote = null;
      this.speaking.reset();
      this.ctx.changed(this);
    }
  }

  // ---- negotiation ----

  private async negotiate(port: PeerPort): Promise<void> {
    if (port !== this.port || port.signalingState !== 'stable') return;
    // The polite side only renegotiates a running session; new sessions are offered by the other side.
    if (this.polite && port.remoteDescription === null) return;
    this.makingOffer = true;
    try {
      await port.setLocalDescription();
    } catch {
      return;
    } finally {
      this.makingOffer = false;
    }
    if (port !== this.port || port.localDescription?.type !== 'offer') return;
    this.sendDescriptionSoon(port, 'offer');
  }

  private async onDescription(description: SessionDescription): Promise<void> {
    if (!this.ctx.media.rtcSupported) return;
    if (description.type === 'rollback') {
      this.onRequest();
      return;
    }
    if (description.type === 'pranswer') return;
    const session = sdpSessionId(description.sdp);

    if (description.type === 'answer') {
      if (!this.port) return;
      if (this.remoteSession !== null && session !== null && session !== this.remoteSession) {
        // Answered by a new connection on the other side: ours cannot continue with it.
        this.startOver();
        return;
      }
    } else if (
      !this.port ||
      (this.remoteSession !== null && session !== null && session !== this.remoteSession)
    ) {
      // A first offer, or the other side started over: a fresh connection for it.
      this.openPort();
    } else if (
      this.port.signalingState === 'stable' &&
      description.sdp !== undefined &&
      description.sdp === this.lastRemoteOffer
    ) {
      // The same offer again: our answer got lost on the way.
      this.resendAnswer();
      return;
    }

    const port = this.port;
    if (!port) return;
    const collision =
      description.type === 'offer' &&
      (this.makingOffer || (port.signalingState !== 'stable' && !this.settingRemoteAnswer));
    if (collision && !this.polite) return; // the impolite side keeps its own offer
    try {
      if (collision) await port.setRemoteDescription({ type: 'rollback' });
      this.settingRemoteAnswer = description.type === 'answer';
      await port.setRemoteDescription(description);
    } catch {
      // A late duplicate or a description of a connection that is gone: nothing to do.
      return;
    } finally {
      this.settingRemoteAnswer = false;
    }
    if (port !== this.port) return;
    this.remoteSession = session ?? this.remoteSession;
    await this.flushCandidates(port);

    if (description.type === 'answer') {
      this.clearTimer('retryTimer');
      return;
    }
    this.lastRemoteOffer = description.sdp ?? null;
    port.adoptAudioTransceiver();
    port.setMic(this.ctx.mic());
    try {
      await port.setLocalDescription();
    } catch {
      return;
    }
    if (port === this.port && port.localDescription?.type === 'answer') {
      this.sendDescriptionSoon(port, 'answer');
    }
  }

  /** The polite side asks for an offer. */
  private onRequest(): void {
    if (this.polite || !this.ctx.active()) return;
    const port = this.port;
    if (port?.signalingState === 'have-local-offer') {
      // An offer is out (or about to go out). If it went out a while ago it may have been
      // lost (the other side was offline longer than the re-sends lasted): once more.
      const offer = port.localDescription;
      const sentLongAgo =
        this.descriptionSent && Date.now() - this.offerSentAt >= (LINK_TIMING.offerRetryMs[0] ?? 0);
      if (offer?.type === 'offer' && sentLongAgo) {
        this.sendDescription(offer);
        this.scheduleOfferRetry(port, 0);
      }
      return;
    }
    if (!port) {
      this.connect();
      return;
    }
    if (port.remoteDescription === null) return; // negotiation is about to start
    if (port.connectionState === 'connected') {
      // Fine on our side: the other side has started over (it saw us leave and come back).
      this.connect();
    } else if (Date.now() - this.recoveredAt >= LINK_TIMING.politeRecoveryMs) {
      // Broken on both sides and we are not fixing it yet.
      this.recover();
    }
  }

  private requestOffer(): void {
    const generation = this.generation;
    this.ctx.send(
      this.peerId,
      REQUEST,
      'high',
      () => this.closed || generation !== this.generation,
    );
  }

  private resendAnswer(): void {
    const description = this.port?.localDescription;
    if (description?.type === 'answer') this.sendDescription(description);
  }

  /** Sends the local description once gathering is complete or the window is over. */
  private sendDescriptionSoon(port: PeerPort, type: 'offer' | 'answer'): void {
    this.descriptionSent = false;
    this.clearTimer('gatherTimer');
    const send = () => {
      this.clearTimer('gatherTimer');
      this.sendPending = null;
      if (port !== this.port || this.descriptionSent) return;
      const description = port.localDescription;
      if (description?.type !== type) return; // rolled back meanwhile
      this.descriptionSent = true;
      this.sendDescription(description);
      if (type === 'offer') this.scheduleOfferRetry(port, 0);
    };
    if (port.iceGatheringState === 'complete') {
      send();
      return;
    }
    this.sendPending = send;
    this.gatherTimer = setTimeout(send, LINK_TIMING.gatherWindowMs);
  }

  private sendDescription(description: SessionDescription): void {
    if (description.type === 'offer') this.offerSentAt = Date.now();
    const generation = this.generation;
    const data: RtcSignal = {
      kind: 'description',
      description:
        description.sdp === undefined
          ? { type: description.type }
          : { type: description.type, sdp: description.sdp },
    };
    this.ctx.send(this.peerId, data, 'high', () => this.closed || generation !== this.generation);
  }

  private scheduleOfferRetry(port: PeerPort, attempt: number): void {
    this.clearTimer('retryTimer');
    const delay = LINK_TIMING.offerRetryMs[attempt];
    if (delay === undefined) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (port !== this.port || port.signalingState !== 'have-local-offer') return;
      const offer = port.localDescription;
      if (offer?.type !== 'offer') return;
      this.sendDescription(offer); // now with every candidate found so far
      this.scheduleOfferRetry(port, attempt + 1);
    }, delay);
  }

  // ---- ICE candidates ----

  private onLocalCandidate(port: PeerPort, candidate: IceCandidate | null): void {
    if (candidate === null) {
      // Gathering complete: send the description now if it still waits.
      this.sendPending?.();
      return;
    }
    if (!this.descriptionSent) return; // it will be part of the description
    const generation = this.generation;
    this.ctx.send(
      this.peerId,
      { kind: 'candidate', candidate },
      'low',
      () => this.closed || generation !== this.generation || port !== this.port,
    );
  }

  private async onRemoteCandidate(candidate: IceCandidate): Promise<void> {
    const port = this.port;
    if (!port) return;
    if (!port.remoteDescription) {
      if (this.pendingCandidates.length < LINK_TIMING.maxPendingCandidates) {
        this.pendingCandidates.push(candidate);
      }
      return;
    }
    await this.addCandidate(port, candidate);
  }

  private async flushCandidates(port: PeerPort): Promise<void> {
    const pending = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of pending) await this.addCandidate(port, candidate);
  }

  private async addCandidate(port: PeerPort, candidate: IceCandidate): Promise<void> {
    const ufrag = sdpIceUfrag(port.remoteDescription?.sdp);
    // A candidate of an earlier ICE generation (before a restart) is of no use.
    if (candidate.usernameFragment && ufrag && candidate.usernameFragment !== ufrag) return;
    try {
      await port.addIceCandidate(candidate);
    } catch {
      // Stale or malformed: ignore.
    }
  }

  // ---- state and recovery ----

  private attachRemote(audio: RemoteAudio): void {
    this.remote?.dispose();
    this.remote = audio;
    audio.setVolume(this.volumeValue);
    this.ctx.changed(this);
  }

  private onConnectionState(state: VoicePeerState): void {
    this.setState(state);
    if (state === 'connected') {
      this.recoveries = 0;
      this.clearTimer('recoveryTimer');
      this.clearTimer('connectTimer');
    } else if (state === 'disconnected') {
      this.scheduleRecovery(LINK_TIMING.disconnectGraceMs);
    } else if (state === 'failed') {
      this.scheduleRecovery(this.polite ? LINK_TIMING.politeRecoveryMs : 0);
    }
  }

  private scheduleRecovery(delayMs: number): void {
    if (this.recoveryTimer !== null || this.closed) return;
    this.recoveryTimer = setTimeout(() => {
      this.recoveryTimer = null;
      this.recover();
    }, delayMs);
  }

  /** The connection should be up some time after it was (re)started; otherwise recover. */
  private armWatchdog(port: PeerPort): void {
    this.clearTimer('connectTimer');
    // The polite side gives the offerer's own recovery a head start.
    const delay =
      LINK_TIMING.connectTimeoutMs * Math.min(4, 2 ** this.recoveries) +
      (this.polite ? LINK_TIMING.politeRecoveryMs : 0);
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      if (port !== this.port || this.closed || port.connectionState === 'connected') return;
      this.setState('failed');
      this.recover();
    }, delay);
  }

  /** First an ICE restart (fresh TURN credentials too), after that a fresh connection. */
  private recover(): void {
    this.clearTimer('recoveryTimer');
    const port = this.port;
    if (this.closed || !port || port.connectionState === 'connected') return;
    const attempt = this.recoveries++;
    this.recoveredAt = Date.now();
    if (this.polite) {
      // Connections are fixed by the offerer; it may not have noticed: tell it.
      this.requestOffer();
      this.armWatchdog(port);
    } else if (attempt === 0 && port.remoteDescription !== null) {
      port.restartIce(this.ctx.iceServers()); // negotiationNeeded → a restart offer
      this.armWatchdog(port);
    } else {
      this.connect();
    }
  }

  private setState(state: VoicePeerState): void {
    if (state === this.stateValue) return;
    this.stateValue = state;
    this.ctx.changed(this);
  }

  private enqueue(task: () => Promise<void>): Promise<void> {
    const next = this.queue.then(() => (this.closed ? undefined : task())).catch(() => {});
    this.queue = next;
    return next;
  }

  private clearTimer(name: 'gatherTimer' | 'retryTimer' | 'recoveryTimer' | 'connectTimer'): void {
    const timer = this[name];
    if (timer !== null) clearTimeout(timer);
    this[name] = null;
  }
}
